import React from "react";
import Column from "@amzn/meridian/column";
import Row from "@amzn/meridian/row";
import Text from "@amzn/meridian/text";
import { Divider, Icon, Tag } from "@amzn/meridian";
import chevronRightSmallTokens from "@amzn/meridian-tokens/base/icon/chevron-right-small";
import clipboardTokens from "@amzn/meridian-tokens/base/icon/clipboard";
import { useNavigate } from "react-router-dom";
import { InspectionListView } from "src/services/inspectionService";
import { completedInspectionStatus } from "src/constants/constants";
import { formatDateTimeWithZone } from "src/utils/dateUtil";
import DraftExpiryFooter from "./DraftExpiryFooter";
import "./landing.scss";

const DESCRIPTION = "AI-guided visual compliance check";

interface RecentInspectionCardProps {
  inspection: InspectionListView;
}

/**
 * A single Recent Inspection card on the Home page, styled to match the
 * Workflows section's AustinInspectionCard (same home-card layout/tag footer),
 * but driven by an InspectionListView (an actual inspection instance) rather
 * than a ScheduledInspection (Austin's schedule metadata) — the two shapes
 * represent different things, so this is a distinct, purpose-built card.
 *
 * Completed inspections navigate to the Inspection Details (review) page;
 * In Progress inspections navigate to the Inspection Resume page. Both routes
 * key off inspectionId directly.
 */
const RecentInspectionCard: React.FC<RecentInspectionCardProps> = ({ inspection }) => {
  const navigate = useNavigate();
  const isCompleted = inspection.status === completedInspectionStatus;

  const handleClick = () => {
    const destination = isCompleted ? "review" : "resume";
    navigate(`/configurationDrivenInspections/${inspection.inspectionId}/${destination}`);
  };

  return (
    <div className="home-card" onClick={handleClick} role="button" style={{ cursor: "pointer" }}>
      <Row alignmentVertical="center" spacing="400" spacingInset="400" alignmentHorizontal="justify">
        <Row className=" home-workflow-icon-bg" spacingInset="400">
          <Icon tokens={clipboardTokens} color="success" />
        </Row>
        <Column spacing="100" width="fill">
          <Text type="h200">{inspection.templateName}</Text>
          <Text type="b200" color="secondary">{DESCRIPTION}</Text>
        </Column>
        <Icon tokens={chevronRightSmallTokens} />
      </Row>

      <div className="home-card-divider">
        <Divider />
      </div>

      <Row alignmentVertical="center" spacing="300" spacingInset="300 400 400">
        {isCompleted ? (
          <CompletedFooter inspection={inspection} />
        ) : inspection.expiresAt ? (
          <DraftExpiryFooter expiresAt={inspection.expiresAt} />
        ) : (
          <>
            <Tag type="warning">In Progress</Tag>
            <Text type="b100" color="secondary">Expired</Text>
          </>
        )}
      </Row>
    </div>
  );
};

/** Completed footer: green "Completed" tag + the submitted (completedAt) timestamp. */
const CompletedFooter: React.FC<{ inspection: InspectionListView }> = ({ inspection }) => {
  const submittedAt = inspection.completedAt ?? inspection.createdAt;
  return (
    <>
      <Tag type="success">Completed</Tag>
      <Text type="b100" color="secondary">{formatDateTimeWithZone(submittedAt)}</Text>
    </>
  );
};

export default RecentInspectionCard;
